import React from "react";
import { Box, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UndoIcon from "@mui/icons-material/Undo";
import { label, TRIAL_RESULT_LABELS } from "../../ProgramLibrary/shared/labels";

export type Entry = {
  id: number; sequence: number; entryType: string; result?: string | null;
  promptLevelName?: string | null; occurredAt?: string | null;
  valueNumeric?: number | null; durationSeconds?: number | null; latencyMilliseconds?: number | null;
  taskStepName?: string | null; note?: string | null;
};

export default function SessionEntryLog({ entries, readOnly, onDelete }: {
  entries: Entry[]; readOnly?: boolean; onDelete?: (e: Entry) => void;
}) {
  if (!entries.length) return <Typography variant="caption" color="text.secondary" sx={{ p: 1, display: "block" }}>No entries yet.</Typography>;
  return (
    <Box sx={{ overflow: "auto", maxHeight: 320 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell width={40}>#</TableCell>
            <TableCell>Time</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Result</TableCell>
            <TableCell>Prompt</TableCell>
            <TableCell>Value</TableCell>
            <TableCell>Step / Note</TableCell>
            {!readOnly && onDelete && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map((e) => (
            <TableRow key={e.id}>
              <TableCell>{e.sequence}</TableCell>
              <TableCell>{e.occurredAt ? new Date(e.occurredAt).toLocaleTimeString() : "—"}</TableCell>
              <TableCell><Typography variant="caption">{e.entryType.replace(/_/g, " ")}</Typography></TableCell>
              {/* Route the raw enum through the shared vocabulary map
                  so the log shows "Correct with Prompt" instead of
                  "prompted". Same source the buttons and counters use. */}
              <TableCell>{label(TRIAL_RESULT_LABELS, e.result, "—")}</TableCell>
              <TableCell>{e.promptLevelName ?? "—"}</TableCell>
              <TableCell>
                {e.durationSeconds != null ? `${e.durationSeconds}s` :
                 e.latencyMilliseconds != null ? `${e.latencyMilliseconds}ms` :
                 e.valueNumeric != null ? String(e.valueNumeric) : "—"}
              </TableCell>
              <TableCell>
                {e.taskStepName && <Typography variant="caption" sx={{ mr: 1 }}>{e.taskStepName}</Typography>}
                {e.note && <Typography variant="caption" color="text.secondary">{e.note}</Typography>}
              </TableCell>
              {!readOnly && onDelete && (
                <TableCell align="right">
                  <Tooltip title="Remove">
                    <IconButton size="small" onClick={() => onDelete(e)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}
